// TodoDropdown.tsx — Raccourci todos dans le header
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dropdown } from '../ui/dropdown/Dropdown';
import { fetchTodos, updateTodo, createTodo } from '../../store/slices/todoSlice';
import { RootState } from '../../store/slices/store';

const TodoDropdown = () => {
  const dispatch = useDispatch<any>();
  const [isOpen, setIsOpen] = useState(false);
  const [newTodo, setNewTodo] = useState('');

  const todos: any[] = useSelector((state: RootState) => (state as any).todo?.todos || []);

  useEffect(() => {
    dispatch(fetchTodos());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pending   = todos.filter(t => !t.completed);
  const completed = todos.filter(t => t.completed);
  const count     = pending.length;

  const toggle = (todo: any) => {
    dispatch(updateTodo({ todoData: { completed: !todo.completed }, todoId: todo.id }));
  };

  const handleAdd = () => {
    const name = newTodo.trim();
    if (!name) return;
    dispatch(createTodo({ title: name, completed: false }));
    setNewTodo('');
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center justify-center text-gray-500 transition-colors bg-white border border-gray-200 rounded-full hover:text-gray-700 h-11 w-11 hover:bg-gray-100 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
        title="Mes tâches"
      >
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-orange-500 text-[10px] font-bold text-white">
            {count > 9 ? '9+' : count}
          </span>
        )}
        {/* Icône check-list */}
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      </button>

      <Dropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        className="absolute -right-[160px] mt-[17px] flex h-auto max-h-[460px] w-[300px] flex-col rounded-xl border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-800 dark:bg-gray-dark lg:right-0"
      >
        {/* En-tête */}
        <div className="flex items-center justify-between pb-3 mb-2 border-b border-gray-100 dark:border-gray-700">
          <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            Tâches
            {count > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 text-xs font-bold">
                {count}
              </span>
            )}
          </h5>
          <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600">
            <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
              <path fillRule="evenodd" clipRule="evenodd"
                d="M6.21967 7.28131C5.92678 6.98841 5.92678 6.51354 6.21967 6.22065C6.51256 5.92775 6.98744 5.92775 7.28033 6.22065L11.999 10.9393L16.7176 6.22078C17.0105 5.92789 17.4854 5.92788 17.7782 6.22078C18.0711 6.51367 18.0711 6.98855 17.7782 7.28144L13.0597 12L17.7782 16.7186C18.0711 17.0115 18.0711 17.4863 17.7782 17.7792C17.4854 18.0721 17.0105 18.0721 16.7176 17.7792L11.999 13.0607L7.28033 17.7794C6.98744 18.0722 6.51256 18.0722 6.21967 17.7794C5.92678 17.4865 5.92678 17.0116 6.21967 16.7187L10.9384 12L6.21967 7.28131Z" />
            </svg>
          </button>
        </div>

        {/* Ajout rapide */}
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={newTodo}
            onChange={e => setNewTodo(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder="Nouvelle tâche…"
            className="flex-1 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-300 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          />
          <button
            onClick={handleAdd}
            className="px-2.5 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded-lg transition-colors"
          >
            +
          </button>
        </div>

        {/* Liste scrollable */}
        <ul className="flex flex-col overflow-y-auto custom-scrollbar divide-y divide-gray-100 dark:divide-gray-800 flex-1">

          {/* En attente */}
          {pending.length > 0 && (
            <>
              <li className="px-1 pt-1 pb-0.5">
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">À faire ({pending.length})</span>
              </li>
              {pending.map((todo: any) => (
                <li key={todo.id} className="flex items-start gap-2.5 px-1 py-2">
                  <button
                    onClick={() => toggle(todo)}
                    className="flex-shrink-0 mt-0.5 w-4 h-4 rounded border-2 border-orange-400 hover:bg-orange-50 transition-colors"
                    title="Marquer comme fait"
                  />
                  <span className="text-xs text-gray-700 dark:text-gray-300 leading-snug">{todo.name || todo.title}</span>
                </li>
              ))}
            </>
          )}

          {/* Complétées */}
          {completed.length > 0 && (
            <>
              <li className="px-1 pt-2 pb-0.5">
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Terminées ({completed.length})</span>
              </li>
              {completed.slice(0, 5).map((todo: any) => (
                <li key={todo.id} className="flex items-start gap-2.5 px-1 py-2">
                  <button
                    onClick={() => toggle(todo)}
                    className="flex-shrink-0 mt-0.5 w-4 h-4 rounded border-2 border-green-400 bg-green-400 flex items-center justify-center hover:bg-green-500 transition-colors"
                    title="Marquer comme à faire"
                  >
                    <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </button>
                  <span className="text-xs text-gray-400 line-through leading-snug">{todo.name || todo.title}</span>
                </li>
              ))}
            </>
          )}

          {todos.length === 0 && (
            <li className="px-1 py-6 text-center text-xs text-gray-400">Aucune tâche</li>
          )}
        </ul>

        {/* Pied */}
        <Link
          to="/MonEspace?tab=todos"
          onClick={() => setIsOpen(false)}
          className="block mt-3 px-4 py-2 text-xs font-medium text-center text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
        >
          Voir toutes les tâches
        </Link>
      </Dropdown>
    </div>
  );
};

export default TodoDropdown;
